// COPIED from Remote-Leverage/hriq@3b50a234afdd packages/design-system/components/ui/v2/button.tsx by apps/web/scripts/sync-design-system.mjs. Do not edit: change it in Hriq and re-sync.
// @ts-nocheck -- type-checked in Hriq under its own compiler options (the CRM's are stricter).
import type { buttonVariants } from "@repo/design-system/components/ui/button";
import { Button as ButtonV1 } from "@repo/design-system/components/ui/button";
import { cn } from "@repo/design-system/lib/utils";
import type { VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";
import type * as React from "react";

/**
 * v2 Button — restyles v1's `Button` (`components/ui/button.tsx`) to match
 * the Figma "Remote Leverage — Design System" file, Button component node
 * `21:315`. All click handling, variants, disabled states, `asChild`
 * support, etc. are v1's own — this file only appends override classes and
 * adds a `loading` prop that v1 has no concept of.
 *
 * Deliberately NOT overridden (already matches Figma via v1's shared base
 * classes, see button.tsx):
 * - focus-visible ring styling
 * - `disabled:opacity-50` (also reused for the `loading` state below)
 *
 * Out of scope (no design exists / no safe way to add):
 * - v1's `success` variant has no Figma v2 design in this file at all —
 *   left completely unstyled by the override map below, so it renders
 *   exactly as it does today under v1's own `buttonVariants`.
 * - Figma's "Black" variant has no v1 equivalent — adding it would mean
 *   either editing v1's cva (forbidden) or typing `variant` wider than
 *   v1's own union (breaks "wrap existing behavior"). Not added.
 */

type ButtonV1Variant = VariantProps<typeof buttonVariants>["variant"];
type ButtonV1Size = VariantProps<typeof buttonVariants>["size"];

const VARIANT_OVERRIDE_CLASSES: Partial<
  Record<string & ButtonV1Variant, string>
> = {
  default: cn(
    "bg-(--v2-button-primary-bg) text-(--v2-button-primary-fg)",
    "shadow-(--v2-button-primary-shadow)",
    "hover:[background:var(--v2-button-primary-bg-hover)]"
  ),
  secondary: cn(
    "bg-(--v2-button-secondary-bg) text-(--v2-button-secondary-fg)",
    "shadow-(--v2-button-secondary-shadow)",
    // Figma's hover for Secondary is a no-op color-wise (a 20%-white-over-
    // white overlay does nothing visible) — only the shadow tightens.
    // Force bg to stay put so it overrides v1's own hover:bg-secondary/80.
    "hover:bg-(--v2-button-secondary-bg) hover:shadow-(--v2-button-secondary-shadow-hover)"
  ),
  outline: cn(
    "border-(--v2-button-outline-border) bg-transparent text-(--v2-button-outline-fg) shadow-none",
    "hover:border-(--v2-button-outline-border-hover) hover:bg-(--v2-button-outline-bg-hover) hover:text-(--v2-button-outline-fg-hover) hover:shadow-(--v2-button-outline-shadow-hover)"
  ),
  ghost: cn(
    "text-(--v2-button-ghost-fg)",
    "hover:bg-(--v2-button-ghost-bg-hover) hover:text-(--v2-button-ghost-fg-hover)"
  ),
  // v1 already has `underline-offset-4 hover:underline` on link — that
  // already produces the right hover look, so only the resting text color
  // needs an override here.
  link: "text-(--v2-button-link-fg)",
  // Figma's hover for Destructive resolves to the same treatment v1
  // already implements (`hover:bg-destructive/90`) — only the resting bg
  // needs an override, hover is left alone on purpose.
  destructive: cn(
    "bg-(--v2-button-destructive-bg) text-(--v2-button-destructive-fg)",
    "shadow-(--v2-button-destructive-shadow)"
  ),
  // `success`: intentionally absent — no Figma v2 design exists for it, so
  // it falls through to v1's own unmodified `buttonVariants` styling.
};

// Figma's Button component only defines default/sm/lg/icon sizes.
// `icon-sm` and `icon-lg` have no Figma node — see the comment below.
const SIZE_OVERRIDE_CLASSES: Partial<Record<string & ButtonV1Size, string>> = {
  default: "h-10 gap-2 rounded-md px-4 py-2 has-[>svg]:px-4",
  sm: "h-9 gap-2 rounded-md px-4 py-2 has-[>svg]:px-4",
  lg: "h-12 gap-2 rounded-[12px] px-8 py-2 has-[>svg]:px-8",
  icon: "size-10 rounded-md",
  // Interpolated, NOT backed by a Figma node: Figma's Button component has
  // no icon-sm/icon-lg size at all. Derived by the same proportional
  // pattern Figma used elsewhere (icon size matches the non-icon size of
  // the same tier's height, radius matches that tier's radius).
  "icon-sm": "size-9 rounded-md",
  "icon-lg": "size-12 rounded-[12px]",
};

type ButtonV2Props = React.ComponentProps<typeof ButtonV1> & {
  /**
   * Ticket-required, v2-only addition — v1 has no loading concept at all.
   * When true, the rendered element is forced `disabled` (reusing v1's
   * existing `disabled:opacity-50 disabled:pointer-events-none` instead of
   * hand-rolling opacity) and a spinning loader icon is rendered before
   * `children`.
   */
  loading?: boolean;
};

function Button({
  className,
  variant = "default",
  size = "default",
  loading = false,
  disabled,
  asChild = false,
  children,
  ...props
}: ButtonV2Props) {
  const variantOverride = variant
    ? VARIANT_OVERRIDE_CLASSES[variant as string & ButtonV1Variant]
    : undefined;
  const sizeOverride = size
    ? SIZE_OVERRIDE_CLASSES[size as string & ButtonV1Size]
    : undefined;

  // Radix `Slot` (what v1 renders under `asChild`) requires exactly one
  // child to clone props onto, so the loading icon can only be injected
  // as a sibling when we're rendering a real <button>. `loading` still
  // forces `disabled` either way.
  const content =
    loading && !asChild ? (
      <>
        <LoaderCircle className="animate-spin" />
        {children}
      </>
    ) : (
      children
    );

  return (
    <ButtonV1
      asChild={asChild}
      className={cn("ds-v2", variantOverride, sizeOverride, className)}
      disabled={disabled || loading}
      size={size}
      variant={variant}
      {...props}
    >
      {content}
    </ButtonV1>
  );
}

export { Button };
export type { ButtonV2Props };
